// Redemption Service
// Implements redemption logic per Loan Agreement Clauses 4 & 5

import { collection, addDoc, query, where, getDocs, updateDoc, doc as firestoreDoc, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Redemption, Series } from '../types';
import { getSeriesById } from './seriesService';
import { getSeriesCrystallisations } from './crystallisationService';

// Configuration from Agreement
const EARLY_REDEMPTION_FEE_RATE = 0.03; // 3%
const PERFORMANCE_FEE_RATE = 0.20; // 20%
const HURDLE_RATE = 0.03; // 3% annualized
const REDEMPTION_NOTICE_DAYS = 30;

/**
 * Check if series is within lock-up period
 */
export const isWithinLockup = (series: Series): boolean => {
  const now = new Date();
  const lockupEnd = series.lockupEnd instanceof Date 
    ? series.lockupEnd 
    : new Date(series.lockupEnd);
  
  return now < lockupEnd;
};

/**
 * Calculate days until lock-up expires
 */
export const getDaysUntilLockupExpiry = (series: Series): number => {
  const now = new Date();
  const lockupEnd = series.lockupEnd instanceof Date 
    ? series.lockupEnd 
    : new Date(series.lockupEnd);
  
  const diffTime = lockupEnd.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return Math.max(0, diffDays);
};

/**
 * Calculate accrued but unpaid performance fee
 * Between last crystallisation and now
 */
export const calculateAccruedPerformanceFee = async (
  series: Series
): Promise<number> => {
  try {
    // Get crystallisation history
    const crystallisations = await getSeriesCrystallisations(series.id);
    
    // Find the last crystallisation
    const lastCrystallisation = crystallisations.length > 0
      ? crystallisations.sort((a, b) => b.date.getTime() - a.date.getTime())[0]
      : null;
    
    // Determine starting NAV
    let startNAV: number;
    let startDate: Date;
    
    if (lastCrystallisation) {
      // Start from last crystallisation's endNAV
      startNAV = lastCrystallisation.endNAV;
      startDate = lastCrystallisation.date;
    } else {
      // No crystallisation yet: start from principal
      startNAV = series.principalAmount;
      startDate = series.issueDate instanceof Date ? series.issueDate : new Date(series.issueDate);
    }
    
    // Current NAV
    const endNAV = series.currentNAV;
    
    // Calculate gross profit
    const grossProfit = endNAV - startNAV;
    
    // If no profit or negative, no performance fee
    if (grossProfit <= 0) {
      return 0;
    }
    
    // Calculate days in period
    const now = new Date();
    const daysInPeriod = Math.floor(
      (now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
    );
    
    // Calculate annualized return
    const annualisedReturnRate = daysInPeriod > 0
      ? (grossProfit / startNAV) * (365 / daysInPeriod)
      : 0;
    
    // Check hurdle rate
    const hurdleRateMet = annualisedReturnRate > HURDLE_RATE;
    
    // Check high water mark
    const highWaterMarkExceeded = endNAV > series.highWaterMark;
    
    // Apply performance fee conditions
    if (grossProfit > 0 && hurdleRateMet && highWaterMarkExceeded) {
      return grossProfit * PERFORMANCE_FEE_RATE;
    }
    
    return 0;
  } catch (error) {
    console.error('Error calculating accrued performance fee:', error);
    return 0;
  }
};

/**
 * Calculate early redemption fee (3% if within lock-up)
 */
export const calculateEarlyRedemptionFee = (
  redemptionValueBeforeFee: number,
  isEarlyRedemption: boolean
): number => {
  if (!isEarlyRedemption) {
    return 0;
  }
  return redemptionValueBeforeFee * EARLY_REDEMPTION_FEE_RATE;
};

/**
 * Calculate complete redemption value per Clause 4.4
 */
export interface RedemptionCalculation {
  // Base values
  tradingNAV: number;
  capitalizedInterest: number;
  totalNAV: number;
  
  // Fees
  accruedPerformanceFee: number;
  earlyRedemptionFee: number;
  
  // Results
  redemptionValueBeforeFees: number;
  redemptionValue: number;
  
  // Lock-up info
  isWithinLockup: boolean;
  lockupEnd: Date;
  daysUntilLockupExpiry: number;
  
  // Dates
  requestDate: Date;
  redemptionDate: Date;
  paymentDueDate: Date;
}

export const calculateRedemptionValue = async (
  series: Series,
  requestDate: Date = new Date(),
  redemptionType: 'full' | 'partial' = 'full',
  redemptionPercentage?: number
): Promise<RedemptionCalculation> => {
  // Determine what portion to redeem
  const percentageToRedeem = redemptionType === 'full' ? 100 : (redemptionPercentage || 100);
  const redemptionRatio = percentageToRedeem / 100;
  
  // Series NAV = trading NAV + capitalized interest
  const fullTradingNAV = series.currentNAV;
  const fullCapitalizedInterest = series.capitalizedInterest || 0;
  const fullTotalNAV = fullTradingNAV + fullCapitalizedInterest;
  
  // Calculate partial amounts
  const tradingNAV = fullTradingNAV * redemptionRatio;
  const capitalizedInterest = fullCapitalizedInterest * redemptionRatio;
  const totalNAV = fullTotalNAV * redemptionRatio;
  
  // Calculate accrued performance fee (on full series)
  const fullAccruedPerformanceFee = await calculateAccruedPerformanceFee(series);
  
  // For partial redemptions, performance fee is proportional
  const accruedPerformanceFee = fullAccruedPerformanceFee * redemptionRatio;
  
  // Check lock-up status
  const withinLockup = isWithinLockup(series);
  const daysUntilExpiry = getDaysUntilLockupExpiry(series);
  
  // Calculate redemption value before early redemption fee
  const redemptionValueBeforeFees = totalNAV - accruedPerformanceFee;
  
  // Calculate early redemption fee if applicable
  const earlyRedemptionFee = calculateEarlyRedemptionFee(
    redemptionValueBeforeFees,
    withinLockup
  );
  
  // Final redemption value
  const redemptionValue = redemptionValueBeforeFees - earlyRedemptionFee;
  
  // Calculate dates
  const redemptionDate = new Date(requestDate);
  redemptionDate.setDate(redemptionDate.getDate() + REDEMPTION_NOTICE_DAYS);
  
  const paymentDueDate = new Date(redemptionDate);
  paymentDueDate.setDate(paymentDueDate.getDate() + 30);
  
  const lockupEnd = series.lockupEnd instanceof Date 
    ? series.lockupEnd 
    : new Date(series.lockupEnd);
  
  return {
    tradingNAV,
    capitalizedInterest,
    totalNAV,
    accruedPerformanceFee,
    earlyRedemptionFee,
    redemptionValueBeforeFees,
    redemptionValue,
    isWithinLockup: withinLockup,
    lockupEnd,
    daysUntilLockupExpiry: daysUntilExpiry,
    requestDate,
    redemptionDate,
    paymentDueDate,
  };
};

/**
 * Validate if redemption can be requested
 */
export interface RedemptionEligibility {
  canRedeem: boolean;
  reason?: string;
  isWithinLockup: boolean;
  daysUntilLockupExpiry: number;
  earlyRedemptionFeeApplies: boolean;
}

export const validateRedemptionEligibility = async (
  seriesId: string
): Promise<RedemptionEligibility> => {
  const series = await getSeriesById(seriesId);
  
  if (!series) {
    return {
      canRedeem: false,
      reason: 'Series not found',
      isWithinLockup: false,
      daysUntilLockupExpiry: 0,
      earlyRedemptionFeeApplies: false,
    };
  }
  
  if (series.status !== 'active') {
    return {
      canRedeem: false,
      reason: `Series is ${series.status}`,
      isWithinLockup: false,
      daysUntilLockupExpiry: 0,
      earlyRedemptionFeeApplies: false,
    };
  }
  
  const withinLockup = isWithinLockup(series);
  const daysUntilExpiry = getDaysUntilLockupExpiry(series);
  
  // Redemption is always allowed, but may incur early redemption fee
  return {
    canRedeem: true,
    isWithinLockup: withinLockup,
    daysUntilLockupExpiry: daysUntilExpiry,
    earlyRedemptionFeeApplies: withinLockup,
  };
};

/**
 * Create redemption request
 */
export const createRedemptionRequest = async (
  seriesId: string,
  lenderId: string,
  redemptionType: 'full' | 'partial' = 'full',
  redemptionPercentage?: number,
  requestedBy: string = 'lender'
): Promise<string> => {
  try {
    const series = await getSeriesById(seriesId);
    if (!series) {
      throw new Error('Series not found');
    }
    
    // Calculate redemption values (will update this function later for partial)
    const calculation = await calculateRedemptionValue(series, undefined, redemptionType, redemptionPercentage);
    
    // Create redemption record
    const redemptionData: Omit<Redemption, 'id'> = {
      lenderId,
      seriesId,
      requestDate: calculation.requestDate,
      redemptionDate: calculation.redemptionDate,
      status: 'pending',
      
      // Redemption type (NEW for Sprint 6B)
      redemptionType,
      redemptionPercentage: redemptionType === 'partial' ? redemptionPercentage : 100, // Use 100 instead of undefined
      
      // Series values (NEW for Sprint 6B)
      seriesNAVBefore: series.currentNAV,
      seriesNAVAfter: redemptionType === 'full' ? 0 : series.currentNAV * (1 - (redemptionPercentage || 100) / 100),
      
      // Calculations
      navAtRedemption: calculation.totalNAV,
      performanceFee: calculation.accruedPerformanceFee,
      earlyRedemptionFee: calculation.earlyRedemptionFee,
      redemptionValue: calculation.redemptionValue,
      
      // Lock-up info
      isEarlyRedemption: calculation.isWithinLockup,
      lockupEnd: calculation.lockupEnd,
      
      // Payment tracking
      paymentStatus: 'pending',
      
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    
    // Save to Firestore
    const docRef = await addDoc(collection(db, 'redemptions'), {
      ...redemptionData,
      requestDate: Timestamp.fromDate(redemptionData.requestDate),
      redemptionDate: Timestamp.fromDate(redemptionData.redemptionDate),
      lockupEnd: Timestamp.fromDate(redemptionData.lockupEnd),
      createdAt: Timestamp.fromDate(redemptionData.createdAt),
      updatedAt: Timestamp.fromDate(redemptionData.updatedAt),
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error creating redemption request:', error);
    throw new Error('Failed to create redemption request');
  }
};

/**
 * Get redemptions by lender
 */
export const getRedemptionsByLender = async (lenderId: string): Promise<Redemption[]> => {
  try {
    const q = query(
      collection(db, 'redemptions'),
      where('lenderId', '==', lenderId)
    );
    
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      requestDate: doc.data().requestDate?.toDate(),
      redemptionDate: doc.data().redemptionDate?.toDate(),
      lockupEnd: doc.data().lockupEnd?.toDate(),
      paymentDate: doc.data().paymentDate?.toDate(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate(),
    })) as Redemption[];
  } catch (error) {
    console.error('Error getting redemptions by lender:', error);
    throw new Error('Failed to fetch redemptions');
  }
};

/**
 * Get redemptions by series
 */
export const getRedemptionsBySeries = async (seriesId: string): Promise<Redemption[]> => {
  try {
    const q = query(
      collection(db, 'redemptions'),
      where('seriesId', '==', seriesId)
    );
    
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      requestDate: doc.data().requestDate?.toDate(),
      redemptionDate: doc.data().redemptionDate?.toDate(),
      lockupEnd: doc.data().lockupEnd?.toDate(),
      paymentDate: doc.data().paymentDate?.toDate(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate(),
    })) as Redemption[];
  } catch (error) {
    console.error('Error getting redemptions by series:', error);
    throw new Error('Failed to fetch redemptions');
  }
};

/**
 * Get all pending redemptions (admin view)
 */
export const getAllPendingRedemptions = async (): Promise<Redemption[]> => {
  try {
    const q = query(
      collection(db, 'redemptions'),
      where('status', 'in', ['pending', 'processing'])
    );
    
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      requestDate: doc.data().requestDate?.toDate(),
      redemptionDate: doc.data().redemptionDate?.toDate(),
      lockupEnd: doc.data().lockupEnd?.toDate(),
      paymentDate: doc.data().paymentDate?.toDate(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate(),
    })) as Redemption[];
  } catch (error) {
    console.error('Error getting pending redemptions:', error);
    throw new Error('Failed to fetch pending redemptions');
  }
};

/**
 * Process redemption (mark as completed)
 */
export const processRedemption = async (
  redemptionId: string,
  processedBy: string,
  notes?: string
): Promise<void> => {
  try {
    const redemptionRef = firestoreDoc(db, 'redemptions', redemptionId);
    
    await updateDoc(redemptionRef, {
      status: 'completed',
      paymentStatus: 'paid',
      paymentDate: Timestamp.fromDate(new Date()),
      processedBy,
      notes: notes || '',
      updatedAt: Timestamp.fromDate(new Date()),
    });
  } catch (error) {
    console.error('Error processing redemption:', error);
    throw new Error('Failed to process redemption');
  }
};

/**
 * Cancel redemption request
 */
export const cancelRedemption = async (
  redemptionId: string,
  reason?: string
): Promise<void> => {
  try {
    const redemptionRef = firestoreDoc(db, 'redemptions', redemptionId);
    
    await updateDoc(redemptionRef, {
      status: 'cancelled',
      notes: reason || 'Cancelled by user',
      updatedAt: Timestamp.fromDate(new Date()),
    });
  } catch (error) {
    console.error('Error cancelling redemption:', error);
    throw new Error('Failed to cancel redemption');
  }
};

/**
 * Format redemption summary for display
 */
export const formatRedemptionSummary = (calculation: RedemptionCalculation): string => {
  const lockupStatus = calculation.isWithinLockup
    ? `🔒 Within Lock-Up (${calculation.daysUntilLockupExpiry} days remaining)`
    : '✅ Lock-Up Expired';
  
  const earlyFeeWarning = calculation.earlyRedemptionFee > 0
    ? `⚠️ Early Redemption Fee: $${calculation.earlyRedemptionFee.toLocaleString()}`
    : '✅ No Early Redemption Fee';
  
  return `
Redemption Summary

Lock-Up Status: ${lockupStatus}
Lock-Up End Date: ${calculation.lockupEnd.toLocaleDateString()}

Request Date: ${calculation.requestDate.toLocaleDateString()}
Redemption Date: ${calculation.redemptionDate.toLocaleDateString()} (30 days)
Payment Due: ${calculation.paymentDueDate.toLocaleDateString()}

Value Breakdown:
Trading NAV: $${calculation.tradingNAV.toLocaleString()}
Capitalized Interest: $${calculation.capitalizedInterest.toLocaleString()}
Total NAV: $${calculation.totalNAV.toLocaleString()}

Deductions:
Accrued Performance Fee: ($${calculation.accruedPerformanceFee.toLocaleString()})
${earlyFeeWarning}

Final Redemption Value: $${calculation.redemptionValue.toLocaleString()}
  `.trim();
};

/**
 * ADMIN FUNCTIONS FOR REDEMPTION MANAGEMENT
 */

/**
 * Approve a redemption request (Admin only)
 * Changes status from 'pending' to 'processing'
 */
export const approveRedemption = async (redemptionId: string): Promise<void> => {
  try {
    const redemptionRef = firestoreDoc(db, 'redemptions', redemptionId);
    await updateDoc(redemptionRef, {
      status: 'processing',
      updatedAt: Timestamp.now(),
    });
    console.log('Redemption approved:', redemptionId);
  } catch (error) {
    console.error('Error approving redemption:', error);
    throw error;
  }
};

/**
 * Reject/Cancel a redemption request (Admin only)
 * Changes status from 'pending' to 'cancelled'
 */
export const rejectRedemption = async (
  redemptionId: string,
  reason: string
): Promise<void> => {
  try {
    const redemptionRef = firestoreDoc(db, 'redemptions', redemptionId);
    await updateDoc(redemptionRef, {
      status: 'cancelled',
      rejectionReason: reason,
      updatedAt: Timestamp.now(),
    });
    console.log('Redemption rejected:', redemptionId);
  } catch (error) {
    console.error('Error rejecting redemption:', error);
    throw error;
  }
};

/**
 * Mark redemption as completed (Admin only)
 * Changes status from 'processing' to 'completed'
 * Records payment date
 * IMPORTANT: Reduces series NAV by redemption amount (funds withdrawn)
 */
export const completeRedemption = async (
  redemptionId: string,
  paymentDate?: Date
): Promise<void> => {
  try {
    // Step 1: Get the redemption details
    const redemptionDoc = await getDocs(
      query(collection(db, 'redemptions'), where('__name__', '==', redemptionId))
    );
    
    if (redemptionDoc.empty) {
      throw new Error('Redemption not found');
    }
    
    const redemptionData = redemptionDoc.docs[0].data();
    const seriesId = redemptionData.seriesId;
    const redemptionValue = redemptionData.redemptionValue;
    
    // Step 2: Get the series
    const series = await getSeriesById(seriesId);
    if (!series) {
      throw new Error('Series not found');
    }
    
    // Step 3: Calculate total amount to remove from series
    // This includes: redemption paid to investor + fees collected by fund
    const performanceFee = redemptionData.performanceFee || 0;
    const earlyFee = redemptionData.earlyRedemptionFee || 0;
    const totalFeesCollected = performanceFee + earlyFee;
    
    // Total to remove from series NAV = investor payment + fund fees
    const totalNAVReduction = redemptionValue + totalFeesCollected;
    const newNAV = series.currentNAV - totalNAVReduction;
    const isFullyRedeemed = Math.abs(newNAV) < 1; // Close if essentially zero
    
    // Calculate proportional principal reduction
    const redemptionRatio = totalNAVReduction / series.currentNAV;
    const principalReduction = series.principalAmount * redemptionRatio;
    const newPrincipal = series.principalAmount - principalReduction;
    
    // Step 4: Update series with reduced NAV and Principal
    const seriesRef = firestoreDoc(db, 'series', seriesId);
    await updateDoc(seriesRef, {
      currentNAV: Math.max(0, newNAV),
      principalAmount: Math.max(0, newPrincipal),
      status: isFullyRedeemed ? 'closed' : 'active',
      updatedAt: Timestamp.now(),
    });
    
    // Step 5: Add fees to fund's fee account (poolNAV collection)
    if (totalFeesCollected > 0) {
      const poolNAVRef = firestoreDoc(db, 'poolNAV', 'current');
      const poolDoc = await getDocs(
        query(collection(db, 'poolNAV'), where('__name__', '==', 'current'))
      );
      
      const currentFundFees = poolDoc.empty ? 0 : (poolDoc.docs[0].data().fundFees || 0);
      
      if (poolDoc.empty) {
        // Create initial poolNAV document
        await addDoc(collection(db, 'poolNAV'), {
          id: 'current',
          fundFees: totalFeesCollected,
          updatedAt: Timestamp.now(),
        });
      } else {
        // Update existing fund fees
        await updateDoc(poolNAVRef, {
          fundFees: currentFundFees + totalFeesCollected,
          updatedAt: Timestamp.now(),
        });
      }
      
      console.log(`Added $${totalFeesCollected} to fund fees account`);
    }

    
    // Step 5: Mark redemption as completed
    const redemptionRef = firestoreDoc(db, 'redemptions', redemptionId);
    await updateDoc(redemptionRef, {
      status: 'completed',
      paymentDate: paymentDate ? Timestamp.fromDate(paymentDate) : Timestamp.now(),
      paymentStatus: 'paid',
      updatedAt: Timestamp.now(),
    });
    
    console.log(`Redemption completed: ${redemptionId}`);
    console.log(`Series ${seriesId} NAV reduced from ${series.currentNAV} to ${newNAV}`);
    if (isFullyRedeemed) {
      console.log(`Series ${seriesId} fully redeemed and closed`);
    }
  } catch (error) {
    console.error('Error completing redemption:', error);
    throw error;
  }
};

/**
 * Get all redemptions (Admin view - no filtering)
 */
export const getAllRedemptions = async (): Promise<Redemption[]> => {
  try {
    const querySnapshot = await getDocs(collection(db, 'redemptions'));
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      requestDate: doc.data().requestDate?.toDate(),
      redemptionDate: doc.data().redemptionDate?.toDate(),
      lockupEnd: doc.data().lockupEnd?.toDate(),
      paymentDate: doc.data().paymentDate?.toDate(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate(),
    })) as Redemption[];
  } catch (error) {
    console.error('Error getting all redemptions:', error);
    throw new Error('Failed to fetch all redemptions');
  }
};
